//import Vue from "vue";
import { createRouter, createWebHistory } from "vue-router";
import AccountWelcome from "@/views/AccountWelcome.vue";
import AppBase from "@/views/AppBase.vue";
import DashboardBase from "@/views/project/dashboard/DashboardBase.vue";
import NoProject from "@/views/basic/NoProject.vue";
import LoginUnauthorized from "@/views/basic/LoginUnauthorized.vue";
import LoginCallback from "@/views/basic/LoginCallback.vue";
import DemoLogin from "@/views/basic/DemoLogin.vue";

import { Permission, PartyType } from "@/stores/user";
import { useUserStore } from "@/stores/user";
import { useAuthStore } from "@/stores/auth";
import { useAppConfigurationStore } from "@/stores/appconfig";
import { useToast } from "vue-toastification";
import { cancelAllRequests } from "@/services/api/httpClient";

const toaster = useToast();

const routes = [
  {
    path: "/",
    name: "Base",
    redirect: "/account", //When a user visits the base URL of the PPM, they will be automatically redirected to the default party
    component: AppBase,
    meta: {},
    children: [
      {
        path: "/party/:partyReference/dashboard",
        name: "Dashboard",
        component: DashboardBase,
        meta: {
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          breadCrumb: { text: "Dashboard", isToplevel: true, to: null },
          commandSearchMenu: {
            label: "Dashboard",
            icon: "mdi-view-dashboard-outline",
            section: "navigatie",
            keywords: "dashboard home overzicht",
            order: 10,
          },
        },
      },
        {
          path: "/party/:partyReference/buildingOverview",
          name: "BuildingOverview",
          component: () => import("@/views/project/BuildingOverview.vue"),
          meta: {
            authorize: [Permission.Manager, Permission.User, Permission.Guest],
            allowedFor: [PartyType.Utility],
            breadCrumb: { text: "Gebouwen", isToplevel: true, to: "BuildingOverview", params: { partyReference: undefined } },
            commandSearchMenu: {
              label: "Gebouwen",
              icon: "mdi-office-building-outline",
              section: "navigatie",
              keywords: "gebouwen building overview",
              order: 20,
            },
          },
        },
      {
        path: "/party/:partyReference/buildings/addcertified",
        name: "BuildingsAddCertified",
        component: () => import("@/views/project/BuildingsAddCertified.vue"),
        props: true,
        meta: {
          afterSwitchProjectGoTo: "Dashboard",
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.Utility],
          breadCrumb: { text: "Erkende maatregelen toevoegen", isToplevel: true, to: "BuildingsAddCertified", params: { partyReference: undefined } },
          commandSearchMenu: {
            label: "Duurzaamheidsmaatregel toevoegen",
            icon: "mdi-plus",
            section: "acties",
            keywords: "maatregel toevoegen erkende maatregelen",
            order: 10,
          },
        },
      },
      {
        path: "/party/:partyReference/complexes",
        name: "ComplexOverview",
        component: () => import("@/views/project/ComplexOverview.vue"),
        meta: {
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.Homes],
          breadCrumb: { text: "Complexen", isToplevel: true, to: "ComplexOverview", params: { partyReference: undefined } },
          commandSearchMenu: {
            label: "Complexen",
            icon: "mdi-home-outline",
            section: "navigatie",
            keywords: "complexen homes overview",
            order: 20,
          },
        },
      },
      {
        path: "/party/:partyReference/newbuildhomes/simulate",
        name: "NewBuildHomesSimulation",
        component: () => import("@/views/project/NewBuildHomesSimulation.vue"),
        props: true,
        meta: {
          afterSwitchProjectGoTo: "Dashboard",
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.Homes],
          breadCrumb: { text: "Nieuwbouwwoningen simuleren", to: "NewBuildHomesSimulation", params: { partyReference: undefined } },
          commandSearchMenu: {
            label: "Nieuwbouwwoningen simuleren",
            icon: "mdi-home-edit-outline",
            section: "acties",
            keywords: "nieuwbouw woningen simuleren complexen homes",
            order: 30,
          },
        },
      },
      {
        path: "/party/:partyReference/buildingDetails/:addressId",
        name: "BuildingDetails",
        component: () => import("@/views/project/BuildingDetails.vue"),
        props: true,
        meta: {
          afterSwitchProjectGoTo: "Dashboard",
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          breadCrumb: { text: "Gebouw details", to: "BuildingDetails", params: { partyReference: undefined } },
        },
      },
      {
        path: "/party/:partyReference/complexDetails/:addressId",
        name: "ComplexDetails",
        component: () => import("@/views/project/ComplexDetails.vue"),
        props: true,
        meta: {
          afterSwitchProjectGoTo: "Dashboard",
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          breadCrumb: { text: "Complex details", to: "ComplexDetails", params: { partyReference: undefined } },
        },
      },
      {
        path: "/party/:partyReference/complexDetails/:addressId/packages",
        name: "MeasurePackageGrid",
        component: () => import("@/views/project/MeasurePackageGrid.vue"),
        props: true,
        meta: {
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          breadCrumb: { text: "Pakketten", to: "MeasurePackageGrid", params: { partyReference: undefined } },
        },
      },
      {
        path: "/party/:partyReference/measureOverview",
        name: "AppliedOverview",
        component: () => import("@/views/project/AppliedOverview.vue"),
        meta: {
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.Utility],
          breadCrumb: { text: "Maatregelen", isToplevel: true, to: "AppliedOverview", params: { partyReference: undefined } },
          commandSearchMenu: {
            label: "Maatregelen",
            icon: "mdi-wrench-outline",
            section: "navigatie",
            keywords: "maatregelen applied overview",
            order: 30,
          },
        },
      },
      {
        path: "party/:partyReference/measures/applied/mutation",
        name: "AppliedMutation",
        component: () => import("@/views/project/AppliedMutation.vue"),
        props: (route: any) => ({
          allow: route.query.allow,
        }),
        meta: {
          afterSwitchProjectGoTo: "AppliedOverview",
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.None, PartyType.Utility, PartyType.Homes],
          breadCrumb: { text: "Maatregel wijzigen", to: "AppliedMutation", params: { partyReference: undefined } },
        },
      },
      {
        path: "/party/:partyReference/measures/applied/:appliedId",
        name: "AppliedDetails",
        component: () => import("@/views/project/AppliedDetails.vue"),
        props: true,
        meta: {
          afterSwitchProjectGoTo: "AppliedOverview",
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.None, PartyType.Utility, PartyType.Homes],
          breadCrumb: { text: "Maatregel details", to: "AppliedDetails", params: { partyReference: undefined } },
        },
      },
      {
        path: "/party/:partyReference/measures/applied/new/:addressId/:definitionId",
        name: "AppliedDetailsNew",
        component: () => import("@/views/project/AppliedDetails.vue"),
        props: true,
        meta: {
          afterSwitchProjectGoTo: "AppliedOverview",
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.None, PartyType.Utility, PartyType.Homes],
          breadCrumb: { text: "Maatregel toevoegen", to: "AppliedDetailsNew" },
        },
      },
      {
        path: "/party/:partyReference/roadmap",
        name: "RoadMapUtility",
        component: () => import("@/views/project/RoadMapUtility.vue"),
        meta: {
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.Utility],
          breadCrumb: { text: "Routekaart", isToplevel: true, to: "RoadMapUtility", params: { partyReference: undefined } },
          commandSearchMenu: {
            label: "Routekaart",
            icon: "mdi-map-outline",
            section: "navigatie",
            keywords: "routekaart utility roadmap",
            order: 40,
          },
        },
      },
      {
        path: "/party/:partyReference/roadmapComplexes",
        name: "RoadMapHome",
        component: () => import("@/views/project/RoadMapHome.vue"),
        meta: {
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.Homes],
          breadCrumb: { text: "Routekaart Complexen", isToplevel: true, to: "RoadMapHome", params: { partyReference: undefined } },
          commandSearchMenu: {
            label: "Routekaart",
            icon: "mdi-map-outline",
            section: "navigatie",
            keywords: "routekaart homes roadmap",
            order: 40,
          },
        },
      },
      {
        path: '/party/:partyReference/dmjop',
        name: 'DMJOP dashboard',
        component: () => import('@/views/project/DmjopDashboard.vue'),
        meta: {
          authorize: [Permission.DmjopPowerUser],
          allowedFor: [PartyType.Homes],
          breadCrumb: { text: "DMJOP", to: "DMJOP dashboard", params: { partyReference: undefined } },
          commandSearchMenu: {
            label: "Importeren van MJOP",
            icon: "mdi-tray-arrow-up",
            section: "acties",
            keywords: "mjop importeren dmjop",
            order: 20,
          },
        }
      },
      {
        path: "/party/:partyReference/powerbi/embed",
        name: "Rapportage",
        component: () => import("@/views/project/PowerBISelector.vue"),
        meta: {
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.Utility, PartyType.Homes],
          breadCrumb: { text: "Rapporten", isToplevel: true, to: "Rapportage", params: { partyReference: undefined } },
          commandSearchMenu: {
            label: "Rapportage",
            icon: "mdi-chart-box-outline",
            section: "navigatie",
            keywords: "rapportage rapporten powerbi",
            order: 50,
          },
        },
      },
      {
        path: "/party/:partyReference/esnobligation",
        name: "ProjectCreateBulkUploadXML",
        component: () => import("@/views/admin/ProjectCreateBulkUploadXML.vue"),
        props: true,
        meta: {
          authorize: [Permission.Manager],
          allowedFor: [PartyType.Utility],
          breadCrumb: { text: "Informatieplicht", isToplevel: true, to: "ProjectCreateBulkUploadXML", params: { partyReference: undefined } },
          commandSearchMenu: {
            label: "Informatieplicht",
            icon: "mdi-file-document-outline",
            section: "navigatie",
            keywords: "informatieplicht beheer",
            order: 60,
          },
        },
      },
      {
        path: "/info",
        name: "Info",
        component: () => import("@/views/basic/ApplicationInfo.vue"),
        meta: {
          authorize: [Permission.Manager],
          breadCrumb: { text: "Informatie", to: "Info" },
          commandSearchMenu: {
            label: "Info",
            icon: "mdi-information-outline",
            section: "navigatie",
            keywords: "info informatie",
            order: 70,
          },
        },
      },
      {
        path: "/party/:partyReference/account/settings",
        name: "AccountSettings",
        component: () => import("@/views/project/AccountSettings.vue"),
        meta: {
          authorize: [Permission.Manager, Permission.User, Permission.Guest],
          allowedFor: [PartyType.None, PartyType.Utility, PartyType.Homes, PartyType.EnergyLabels],
          breadCrumb: { text: "Instellingen", isToplevel: true, to: "AccountSettings", params: { partyReference: undefined } },
        },
      }
    ],
  },
  {
    path: "/account",
    name: "Account",
    component: AccountWelcome,
    meta: {
      public: true,
      onlyWhenLoggedOut: true,
      breadCrumb: { text: "Account", to: "Account" },
    },
  },
  {
    path: "/demo",
    name: "DemoUtility",
    component: DemoLogin,
    props: { type: PartyType.Utility },
    meta: {
      public: true,
      breadCrumb: { text: "Demo Utiliteiten", to: "DemoUtility" },
    },
  },
  {
    path: "/demowoningen",
    name: "DemoHomes",
    component: DemoLogin,
    props: { type: PartyType.Homes },
    meta: {
      public: true,
      breadCrumb: { text: "Demo Woningen", to: "DemoHomes" },
    },
  },
  {
    path: "/callback",
    name: "Callback",
    component: LoginCallback,
    meta: {
      public: true,
    },
  },
  {
    path: "/unauthorized",
    name: "Unauthorized",
    component: LoginUnauthorized,
  },
  {
    path: "/noproject",
    name: "NoProject",
    component: NoProject,
  },
  {
    path: "/silent-renew",
    name: "SilentRenew",
    component: () => import("@/views/basic/SilentRenew.vue"),
    meta: {
      public: true,
    },
  },

  {
    path: "/switchproject/:partyReference",
    name: "SwitchProject",
    meta: {
      authorize: [Permission.Manager, Permission.User, Permission.Guest],
    },
  },
];

const router = createRouter({
  history: createWebHistory(), // Use createWebHistory for history mode
  routes,
});

router.beforeEach(async (to: any, from: any, next: any) => {
  // console.log("%cbeforeEach, from: ", "background: orange; color: black", from);
  // console.log("%cbeforeEach, to: ", "background: orange; color: black", to);
  //console.log("beforeEach to  ", to);
  // console.log("beforeEach from", from);
  // console.log("beforeEach to.meta.breadCrumb", to.meta.breadCrumb);
  // console.log("beforeEach next", next);
  const isPublic = to.matched.some((record) => record.meta.public);
  const onlyWhenLoggedOut = to.matched.some((record) => record.meta.onlyWhenLoggedOut);
  const authStore = useAuthStore();
  const userStore = useUserStore();
  cancelAllRequests(`Navigating from ${from.path} to ${to.path}`);
  if (to.meta.breadCrumb) {
    if (to.meta.breadCrumb.isToplevel) {
      userStore.resetBreadcrumb(to);
    } else {
      userStore.pushBreadcrumb(to);
    }
  }

  const loggedIn = await authStore.isLoggedIn();
  const { authorize } = to.meta;
  if (!isPublic && !loggedIn) {
    if (to.name == "Dashboard") {
      //console.log("beforeEach next => account");
      return next({ name: "Account" });
    } else {
      //console.log("beforeEach next => account redirect");
      return next({
        name: "Account",
        query: { redirect: to.fullPath }, // Store the full path to redirect the user to after login
      });
    }
  } else {
    const userStore = useUserStore();
    // console.log("beforeEach authorize: ", authorize);
    if (authorize) {
      if (!loggedIn) {
        // not logged in so redirect to login page with the return url
        return next({ name: "Account", query: { returnUrl: to.path } });
      }
      const authorised = await userStore.isAuthorized(authorize);
      // console.log("%cbeforeEach, to.meta, to.params.partyReference, userStore.currentPartyReference: ", "background: orange; color: black", to.meta, to.params.partyReference, userStore.currentPartyReference);
      if (to.params.partyReference != undefined && userStore.currentPartyReference != to.params.partyReference) {
        const status = await userStore.setParty(to.params.partyReference);
        if (status != 204) {
          // The target route is already being resolved. Redirecting to it starts a
          // second navigation, which cancels requests triggered by the party update.
          return next();
        } else {
          return next({ name: "Unauthorized" });
        }
      }
      // check if route is restricted by role
      if (authorize.length && !authorised) {
        // Indien men voor een pagina niet geautoriseerd is wordt geredirect naar de Dashboardpagina.
        // Als men ook daar niet voor geautoriseerd is wordt pas geredirect naar /unauthorized.
        if (to.name == "Dashboard") {
          toaster.error("U bent niet geautoriseerd voor deze pagina.");
          return next({ name: "Unauthorized" });
        } else {
          return next({ name: "Dashboard", params: { partyReference: userStore.currentPartyReference } });
        }
      } else {
        if (to.name == "SwitchProject" && userStore.isPartyAllowed(to.params.partyReference)) {
          // console.log("%cbeforeEach switchProject", "background: orange; color: black", to.params.partyReference);
          await userStore.setParty(to.params.partyReference);
          return next({ name: "Dashboard", params: { partyReference: to.params.partyReference } });
        }
      }
    } else {
      if (loggedIn && onlyWhenLoggedOut) {
        await userStore.checkStoreState();
        return next({ name: "Dashboard", params: { partyReference: userStore.currentPartyReference } });
      }
    }
  }

  // in AppliedMutation mag je alleen maar komen via een button click, geen direct link
  if (to.name == "AppliedMutation") {
    if (!to.query.allow) {
      return next({ name: "AppliedOverview" });
    }
  }

  const appconfigStore = useAppConfigurationStore();
  document.title = appconfigStore.title;

  next();
});

export default router;


